import { landingImages } from './landingVisual'
import { landingBCriticalAssets } from './landingBVisual'
import type {
  DesignVariantConfig,
  SurfaceDescriptor,
  SurfaceKey,
} from '../types/experience'

/**
 * The design variant register.
 *
 * Two surfaces are presented through two approved designs each. This file
 * describes them and nothing else — it holds no timer, no state and no
 * storage. What is currently LIVE is configuration an administrator publishes
 * (see services/designVariantStore.ts), not a constant edited in code.
 */

const MINUTE = 60 * 1000

/** Intervals an administrator can choose, named as they read on screen. */
export const ROTATION_PRESETS = [
  { label: '1 minute', ms: MINUTE },
  { label: '5 minutes', ms: 5 * MINUTE },
  { label: '10 minutes', ms: 10 * MINUTE },
  { label: '15 minutes', ms: 15 * MINUTE },
  { label: '30 minutes', ms: 30 * MINUTE },
  { label: '1 hour', ms: 60 * MINUTE },
  { label: '2 hours', ms: 120 * MINUTE },
  { label: '6 hours', ms: 360 * MINUTE },
  { label: '12 hours', ms: 720 * MINUTE },
  { label: '24 hours', ms: 1440 * MINUTE },
] as const

export function intervalLabel(ms: number): string {
  const preset = ROTATION_PRESETS.find((option) => option.ms === ms)
  if (preset) return preset.label
  const minutes = Math.round(ms / MINUTE)
  return minutes >= 60
    ? `${Math.round((minutes / 60) * 10) / 10} hours`
    : `${minutes} minutes`
}

export const SURFACES: readonly SurfaceDescriptor[] = [
  {
    key: 'HOME',
    label: 'Homepage',
    description:
      'The public front door. Seen by people who do not have an account yet, so it carries the whole first impression.',
    path: '/welcome',
    preservedState: [
      'Scroll is returned to the top, because the two designs are structured differently and a position deep in one is meaningless in the other',
      'Sign-in state, session and any open navigation',
      'Campus register and every figure read from it',
    ],
    designs: [
      {
        id: 'DESIGN_1',
        name: 'The campus operating system',
        summary:
          'Editorial. Product-led hero, capability grid marked by what needs an account, trust explained, four audience paths into one account.',
        criticalAssets: [
          landingImages.heroStudent,
          landingImages.heroCampus,
          landingImages.ctaStudents,
        ],
      },
      {
        id: 'DESIGN_2',
        name: 'The digital world of campus life',
        summary:
          'Photographic. Full-bleed hero with the product in the scene, six campus facets, four deep-green product stripes, one-account credential.',
        criticalAssets: landingBCriticalAssets,
      },
    ],
  },
  {
    key: 'HOUSING_FIND_HOME',
    label: 'Housing — Find a home',
    description:
      'Where a student searches for somewhere to live. State here is expensive to rebuild, so a handover must never cost any of it.',
    path: '/housing',
    preservedState: [
      'Search query, campus, budget, move-in date, accommodation type and every advanced filter',
      'Sort order, result set and the shortlist',
      'Selected property, hovered property and the map centre and zoom',
      'List / split / map preference and scroll position',
    ],
    designs: [
      {
        id: 'DESIGN_1',
        name: 'Editorial workspace',
        summary:
          'Breadcrumb, result count beside the heading, applied-filter chips, wide property cards against a narrower map column, closing trust panel.',
        criticalAssets: [],
      },
      {
        id: 'DESIGN_2',
        name: 'Discovery finder',
        summary:
          '“Find a home that fits your life”, search-led, seven category tabs, the same cards beside a map carrying an honest commute note.',
        criticalAssets: [],
      },
    ],
  },
  {
    key: 'DISCOVER',
    label: 'Discover — universities and programmes',
    description:
      'Where someone chooses where to study. Long sessions with a comparison in progress, so a handover must cost nothing.',
    path: '/discover',
    preservedState: [
      'Search, scope, field, location and every applied filter',
      'Sort order and grid / list / map view',
      'Saved universities, programmes and scholarships',
      'The comparison set and the open section',
    ],
    designs: [
      {
        id: 'DESIGN_1',
        name: 'Editorial discovery',
        summary:
          'Full hero with the search as the largest control, recommended entry cards, wide institution cards with programmes and the next deadline.',
        criticalAssets: [],
      },
      {
        id: 'DESIGN_2',
        name: 'Discovery workspace',
        summary:
          'Personalised opener, seven-item discovery nav, three-up institution grid, a permanent refine rail and grid / list / map controls.',
        criticalAssets: [],
      },
    ],
  },
]

export function surfaceFor(key: SurfaceKey): SurfaceDescriptor {
  const found = SURFACES.find((surface) => surface.key === key)
  if (!found) throw new Error(`Unknown design surface: ${key}`)
  return found
}

/**
 * What a surface falls back to before configuration has loaded, and if it
 * never loads. Design 1 is the safe design on every surface.
 */
export const DEFAULT_CONFIGS: Record<SurfaceKey, DesignVariantConfig> = {
  HOME: {
    surface: 'HOME',
    mode: 'ROTATE',
    design1Enabled: true,
    design2Enabled: true,
    rotationIntervalMs: 60 * MINUTE,
    rotationStartDesign: 'DESIGN_1',
    rotationPaused: false,
    pausedOn: null,
    fallbackDesign: 'DESIGN_1',
    updatedAt: '2026-08-16T09:00:00.000Z',
    updatedBy: 'Platform default',
  },
  HOUSING_FIND_HOME: {
    surface: 'HOUSING_FIND_HOME',
    mode: 'ROTATE',
    design1Enabled: true,
    design2Enabled: true,
    rotationIntervalMs: 10 * MINUTE,
    rotationStartDesign: 'DESIGN_1',
    rotationPaused: false,
    pausedOn: null,
    fallbackDesign: 'DESIGN_1',
    updatedAt: '2026-08-16T09:00:00.000Z',
    updatedBy: 'Platform default',
  },
  DISCOVER: {
    surface: 'DISCOVER',
    mode: 'ROTATE',
    design1Enabled: true,
    design2Enabled: true,
    rotationIntervalMs: 60 * MINUTE,
    rotationStartDesign: 'DESIGN_1',
    rotationPaused: false,
    pausedOn: null,
    fallbackDesign: 'DESIGN_1',
    updatedAt: '2026-08-16T09:00:00.000Z',
    updatedBy: 'Platform default',
  },
}

/**
 * The query parameter that pins a design for review: ?designPreview=1|2|rotation.
 * It changes nothing for anyone else and no control for it is rendered.
 */
export const PREVIEW_PARAM = 'designPreview'

/** Where each visitor's rotation anchor is kept, per surface. */
export function anchorKey(surface: SurfaceKey): string {
  return `campuzon.design.anchor.${surface}`
}
